import { IModel, IAttribute, ModelConstants, stringIsNullOrEmpty } from 'shell-models-lib';
import { Metamodel } from 'app/model/base/model';
import { Activity } from './activity';

import { ParameterConfig } from 'app/model/pipeline/parameter-config';
import { ParameterConfigWrapper } from 'app/model/pipeline/parameter-config-wrapper';
import { format } from 'app/shared/utils/string.util';
import { AzureMLServiceLinkedService } from 'app/model/linkedservice/azure-ml-service.linkedservice';
import { createParameterConfigurationValidator } from 'app/model/adapters/linkedservices/parameter-config.util';
import { IViolation } from '../base/annotation';
import { Copier } from 'app/model/base/copier';

export enum MachineLearningPipelineType {
    ML_PIPELINE_ID = 'mlPipelineId',
    ML_PIPELINE_ENDPOINT_ID = 'mlPipelineEndpointId'
}

export enum MachineLearningPipelineEndpointInputFormat {
    ML_PIPELINE_ENDPOINT_NAME = 'Endpoint name',
    ML_PIPELINE_ENDPOINT_ID = 'Endpoint id'
}
export enum MachineLearningExecutionType {
    ML_PIPELINE = 'pipeline',
    ML_BATCH_DEPLOYMENT = 'batch deployment'
}

@Metamodel.model({
    name: ModelConstants.amlExecutePipelineActivity,
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.AzureMLExecutePipelineActivity_DisplayText),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.AzureMLExecutePipelineActivity_DisplayText)
    ],
    referable: true
})
export class AzureMLExecutePipelineActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly ML_PIPELINE_ID: IAttribute;
    public static readonly ML_PIPELINE_ENDPOINT_ID: IAttribute;
    public static readonly VERSION: IAttribute;
    public static readonly DATA_PATH_ASSIGNMENTS: IAttribute;
    public static readonly EXPERIMENT_NAME: IAttribute;
    public static readonly ML_PIPELINE_PARAMETERS: IAttribute;
    public static readonly ML_PARENT_RUN_ID: IAttribute;
    public static readonly ML_BATCH_ENDPOINT_NAME: IAttribute;
    public static readonly ML_BATCH_DEPLOYMENT_NAME: IAttribute;
    public static readonly ML_JOB_SETTINGS: IAttribute;
    public static readonly ML_JOB_INPUTS: IAttribute;
    public static readonly ML_JOB_OUTPUTS: IAttribute;
    public static readonly ML_EXECUTION_TYPE: IAttribute;
    public pipelineFormat: string = MachineLearningPipelineType.ML_PIPELINE_ID;
    public pipelineEndpointInputFormat: string = MachineLearningPipelineEndpointInputFormat.ML_PIPELINE_ENDPOINT_NAME;

    @Metamodel.attribute({
        display: __IS_FABRIC_DPE__
            ? stringResources.AMLService_ConnectionLabel
            : format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureMLService_DisplayText),
        constraint: {
            required: true
        },
        reference: true,
        type: AzureMLServiceLinkedService.MODEL
    })
    public linkedService?: AzureMLServiceLinkedService;

    @Metamodel.attribute({
        display: stringResources.ModelName_MLPipelineId,
        association: 'ML_PIPELINE_ID',
        templatable: true,
        constraint: {
            validator: AzureMLExecutePipelineActivity.validatePipelineIdRequired
        }
    })
    public mlPipelineId?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_MLPipelineEndpointId,
        association: 'ML_PIPELINE_ENDPOINT_ID',
        templatable: true,
        constraint: {
            validator: AzureMLExecutePipelineActivity.validatePipelineEndpointIdRequired
        }
    })
    public mlPipelineEndpointId?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_MLPipelineEndpointIdVersion,
        association: 'VERSION',
        templatable: true
    })
    public version?: string;

    @Metamodel.attribute({
        templatable: true,
        association: 'DATA_PATH_ASSIGNMENTS',
        display: stringResources.ModelName_MLPipelineDataPathAssignments
    })
    public dataPathAssignments?: ParameterConfigWrapper[] = [];

    @Metamodel.attribute({
        display: stringResources.ModelName_MLExperimentName,
        association: 'EXPERIMENT_NAME',
        constraint: {
            required: false
        },
        templatable: true
    })
    public experimentName?: string;

    @Metamodel.attribute({
        templatable: true,
        association: 'ML_PIPELINE_PARAMETERS',
        display: stringResources.ModelName_MLPipelineParameters,
        accepts: ['Array'],
        constraint: {
            validator: createParameterConfigurationValidator()
        }
    })
    public mlPipelineParameters?: ParameterConfig[];

    @Metamodel.attribute({
        display: stringResources.ModelName_MLParentRunId,
        association: 'ML_PARENT_RUN_ID',
        constraint: {
            required: false
        },
        templatable: true
    })
    public mlParentRunId?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_MLContinueOnStepFailure,
        constraint: {
            required: false
        },
        templatable: true
    })
    public continueOnStepFailure?: boolean;

    @Metamodel.attribute({
        display: stringResources.ModelName_MLExecutionType,
        association: 'ML_EXECUTION_TYPE',
        templatable: true
    })
    public mlExecutionType?: string = __IS_FABRIC_DPE__
        ? MachineLearningExecutionType.ML_BATCH_DEPLOYMENT
        : MachineLearningExecutionType.ML_PIPELINE;

    @Metamodel.attribute({
        display: stringResources.ModelName_MLBatchEndpoint,
        association: 'ML_BATCH_ENDPOINT_NAME',
        templatable: true,
        constraint: {
            validator: AzureMLExecutePipelineActivity.validateBatchEndpointRequired
        }
    })
    public mlBatchEndpointName?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_MLBatchDeployment,
        association: 'ML_BATCH_DEPLOYMENT_NAME',
        templatable: true
    })
    public mlBatchDeploymentName?: string;

    @Metamodel.attribute({
        templatable: true,
        association: 'ML_JOB_INPUTS',
        display: stringResources.ModelName_MLJobInputs
    })
    public jobInputs?: ParameterConfigWrapper[] = [];

    @Metamodel.attribute({
        templatable: true,
        association: 'ML_JOB_OUTPUTS',
        display: stringResources.ModelName_MLJobOutputs
    })
    public jobOutputs?: ParameterConfigWrapper[] = [];

    @Metamodel.attribute({
        templatable: true,
        association: 'ML_JOB_SETTINGS',
        display: stringResources.ModelName_MLJobSettings,
        accepts: ['Array'],
        constraint: {
            validator: createParameterConfigurationValidator()
        }
    })
    public jobSettings?: ParameterConfig[];

    constructor(name?: string, description?: string) {
        super(name, description);
        this.activityType = 'ExecutionActivity';
    }

    private static validateBatchEndpointRequired(
        _mlActivity?: AzureMLExecutePipelineActivity,
        mlBatchEndpointName?: string
    ): IViolation[] | undefined {
        if (
            _mlActivity?.mlExecutionType === MachineLearningExecutionType.ML_BATCH_DEPLOYMENT &&
            stringIsNullOrEmpty(mlBatchEndpointName)
        ) {
            return [
                {
                    message: () => format(stringResources.RequiredTemplate, stringResources.ModelName_MLBatchEndpoint)
                }
            ];
        }
        return undefined;
    }

    private static validatePipelineIdRequired(
        _mlActivity?: AzureMLExecutePipelineActivity,
        mlPipelineId?: string
    ): IViolation[] | undefined {
        if (
            _mlActivity?.mlExecutionType === MachineLearningExecutionType.ML_PIPELINE &&
            _mlActivity?.pipelineFormat === MachineLearningPipelineType.ML_PIPELINE_ID &&
            stringIsNullOrEmpty(mlPipelineId)
        ) {
            return [
                {
                    message: () => format(stringResources.RequiredTemplate, stringResources.ModelName_MLPipelineId)
                }
            ];
        }
        return undefined;
    }

    private static validatePipelineEndpointIdRequired(
        _mlActivity?: AzureMLExecutePipelineActivity,
        mlPipelineEndpointId?: string
    ): IViolation[] | undefined {
        if (
            _mlActivity?.mlExecutionType === MachineLearningExecutionType.ML_PIPELINE &&
            _mlActivity?.pipelineFormat === MachineLearningPipelineType.ML_PIPELINE_ENDPOINT_ID &&
            stringIsNullOrEmpty(mlPipelineEndpointId)
        ) {
            return [
                {
                    message: () =>
                        format(stringResources.RequiredTemplate, stringResources.ModelName_MLPipelineEndpointId)
                }
            ];
        }
        return undefined;
    }

    public onActivityCloned(clonedActivity: AzureMLExecutePipelineActivity, context: { inlineDataset?: boolean }): void {
        if (__IS_FABRIC_DPE__) {
            if (this.linkedService?.parameterized) {
                clonedActivity.linkedService = Copier.clone(
                    context,
                    [this.linkedService],
                    undefined,
                    true
                )[0] as AzureMLServiceLinkedService;
            }
        }
    }
}
